import { useState } from 'react';
import { format } from 'date-fns';
import { Flame, CheckCircle2, AlertCircle, Edit3, Download, Paperclip, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, API_URL } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function BoilerReportCard({
  jobId,
  hasXeroInvoice,
  report,
  onEdit,
}: {
  jobId: string;
  hasXeroInvoice?: boolean;
  report: any;
  onEdit: () => void;
}) {
  const { isAdmin } = useAuth();
  const checks = report.checks || {};

  const [downloading, setDownloading] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const downloadPdf = async () => {
    if (!report.id) {
      toast.error('This certificate has no id yet — please reopen the job and try again.');
      return;
    }
    setDownloading(true);
    try {
      // Fetch as blob so we can save it locally with the right filename
      const token = localStorage.getItem('auth_token');
      const url = `${API_URL}/jobs/${jobId}/service-reports/${report.id}/pdf`;
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();

      // derive filename from content-disposition, fall back to a sensible default
      let filename = 'boiler-service-cert.pdf';
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      if (m) filename = m[1];

      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Delay revoke a beat so mobile Safari has time to consume it
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const attachToXero = async () => {
    if (!report.id) {
      toast.error('Certificate has no id yet.');
      return;
    }
    setAttaching(true);
    try {
      await api.post(`/jobs/${jobId}/service-reports/${report.id}/attach-to-xero`);
      toast.success('Cert attached to the Xero invoice');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to attach to Xero');
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="card p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Flame className="h-4 w-4 text-red-600" />
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">
            Boiler service report
          </h3>
          {report.safe_to_use ? (
            <span className="chip bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200">
              <CheckCircle2 className="h-3 w-3" /> Safe to use
            </span>
          ) : (
            <span className="chip bg-red-100 text-red-800 ring-1 ring-inset ring-red-200">
              <AlertCircle className="h-3 w-3" /> Not safe
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={downloadPdf}
            disabled={downloading}
            className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 disabled:opacity-50"
            title="Download branded PDF"
          >
            {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            PDF
          </button>
          {isAdmin && hasXeroInvoice && (
            <button
              onClick={attachToXero}
              disabled={attaching}
              className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 disabled:opacity-50"
              title="Attach this cert to the Xero invoice"
            >
              {attaching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
              Attach to Xero
            </button>
          )}
          <button
            onClick={onEdit}
            className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100"
          >
            <Edit3 className="h-3 w-3" /> Edit
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Row label="Appliance" value={`${report.appliance_make || ''} ${report.appliance_model || ''}`.trim()} />
        <Row label="Serial" value={report.serial_number || '—'} />
        <Row label="Type" value={report.boiler_type} />
        <Row label="Fuel" value={report.gas_type} />
        {report.location && <Row label="Location" value={report.location} />}
        {report.operating_pressure && <Row label="Op. pressure" value={`${report.operating_pressure} bar`} />}
        {report.co_ppm && <Row label="CO" value={`${report.co_ppm} ppm`} />}
        {report.co2_pct && <Row label="CO₂" value={`${report.co2_pct} %`} />}
      </div>

      {(() => {
        const passedItems = Object.entries(checks).filter(([, v]) => v);
        if (!passedItems.length) return null;
        return (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="font-semibold text-slate-500 uppercase tracking-wider">
                Safety checks
              </span>
              <span className="font-mono text-slate-600">
                {passedItems.length} completed
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {passedItems.map(([c]) => (
                <div key={c} className="flex items-center gap-1.5 text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-slate-700">{c}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {report.faults && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-100 p-3">
          <div className="text-xs font-bold text-red-800 uppercase tracking-wider mb-1">Faults</div>
          <p className="text-sm text-red-900 whitespace-pre-wrap">{report.faults}</p>
        </div>
      )}
      {report.recommendations && (
        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-100 p-3">
          <div className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1">Recommendations</div>
          <p className="text-sm text-amber-900 whitespace-pre-wrap">{report.recommendations}</p>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
        <span>
          <strong className="text-slate-700">{report.engineer || '—'}</strong>
        </span>
        <span>
          {report.completed_at ? format(new Date(report.completed_at), 'd MMM yyyy, HH:mm') : ''}
        </span>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">{label}</span>
      <span className="text-slate-900 font-medium truncate">{value || '—'}</span>
    </div>
  );
}
